import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/layout/AppShell';
import { Skeleton } from './components/ui/primitives';
import { useAuth } from './lib/auth';
import { LoginPage } from './pages/LoginPage';
import { FleetPage } from './pages/FleetPage';
import { AirshipDetailPage } from './pages/AirshipDetailPage';
import { NewReservationPage } from './pages/NewReservationPage';
import { MyReservationsPage } from './pages/MyReservationsPage';
import { ReservationDetailPage } from './pages/ReservationDetailPage';
import { ApprovalsPage } from './pages/ApprovalsPage';
import { DispatchPage } from './pages/DispatchPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { NotificationHealthPage } from './pages/NotificationHealthPage';
import { AuditPage } from './pages/AuditPage';
import { AdminPage } from './pages/AdminPage';

// The globe and the charts carry three.js and recharts; keep them out of the
// initial bundle so login and the lists stay fast.
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage })),
);
const AnalyticsPage = lazy(() =>
  import('./pages/AnalyticsPage').then((module) => ({ default: module.AnalyticsPage })),
);

function RequireAuth({ children, staffOnly = false }: { children: React.ReactNode; staffOnly?: boolean }) {
  const { status, isStaff } = useAuth();

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <Skeleton className="h-40 w-full max-w-md" />
      </div>
    );
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace />;
  }
  if (staffOnly && !isStaff) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<Skeleton className="h-[520px] w-full" />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route path="fleet" element={<FleetPage />} />
        <Route path="fleet/:id" element={<AirshipDetailPage />} />
        <Route path="reservations/new" element={<NewReservationPage />} />
        <Route path="reservations" element={<MyReservationsPage />} />
        <Route path="reservations/:id" element={<ReservationDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />

        <Route
          path="dispatch"
          element={
            <RequireAuth staffOnly>
              <DispatchPage />
            </RequireAuth>
          }
        />
        <Route
          path="approvals"
          element={
            <RequireAuth staffOnly>
              <ApprovalsPage />
            </RequireAuth>
          }
        />
        <Route
          path="analytics"
          element={
            <RequireAuth staffOnly>
              <Suspense fallback={<Skeleton className="h-96 w-full" />}>
                <AnalyticsPage />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route
          path="notification-health"
          element={
            <RequireAuth staffOnly>
              <NotificationHealthPage />
            </RequireAuth>
          }
        />
        <Route
          path="audit"
          element={
            <RequireAuth staffOnly>
              <AuditPage />
            </RequireAuth>
          }
        />
        <Route
          path="admin"
          element={
            <RequireAuth staffOnly>
              <AdminPage />
            </RequireAuth>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
